import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import useAccessTokenData from "@/hooks/use-access-token-data";
import useMeQuery from "@/hooks/use-me-query";
import { useSwipeGestures } from "@/hooks/use-swipe-gestures";
import useUserSwitchAnimation from "@/hooks/use-user-switch-animation";
import selectUser from "@/utils/select-user";
import { Loader2 } from "lucide-react";
import { FC, PropsWithChildren, Suspense } from "react";
import { Link } from "react-router";
import AnimatedContentWrapper from "./animated-content-wrapper";
import BackTrigger from "./back-trigger";
import NavUser from "./nav-user";
import OpenMeButton from "./open-me-button";
import SkipButton from "./skip-button";
import UserSwitchButton from "./user-switch-button";

const HomeLayout: FC<
  PropsWithChildren<{
    title: string;
    backTrigger?: boolean;
  }>
> = ({ children, title, backTrigger }) => {
  const { data } = useMeQuery();
  const user = selectUser(data);
  const accessTokenData = useAccessTokenData();

  const {
    isAnimating,
    animationDirection,
    swipeMode,
    handleSkip,
    handleSwipeLeft,
    handleSwipeRight,
    resetAnimation,
    toggleSwipeMode,
    disableSwipeMode,
  } = useUserSwitchAnimation();

  const swipeHandlers = useSwipeGestures({
    onSwipeLeft: handleSwipeLeft,
    onSwipeRight: handleSwipeRight,
  });

  return (
    <div className="flex flex-col min-h-screen" {...swipeHandlers}>
      <header className="sticky top-0 gap-2 flex shrink-0 items-center justify-between bg-background p-4 z-40">
        <div className="flex w-full gap-2">
          {backTrigger && <BackTrigger />}
          {!swipeMode && (
            <h1 className="text-xl w-full truncate font-semibold">{title}</h1>
          )}
          <SkipButton onSkip={handleSkip} />
          <UserSwitchButton onActivate={toggleSwipeMode} isActive={swipeMode} />

          {swipeMode && (
            <div className="flex items-center gap-2 px-2 py-1 bg-primary/10 rounded-md select-none">
              <button
                onClick={disableSwipeMode}
                className="text-xs text-primary hover:text-primary/80 px-2 py-1 rounded hover:bg-primary/20 select-none"
              >
                Відключити
              </button>
            </div>
          )}
        </div>

        <div className="flex flex-1 gap-2 items-center">
          {accessTokenData?.userId !== accessTokenData?.id && <OpenMeButton />}
          <Avatar className="h-8 w-8 rounded-full cursor-pointer" asChild>
            <Link to="/settings">
              <AvatarImage src={user?.avatar} alt={String(user?.name)} />
              <AvatarFallback className="rounded-lg">
                {String(user?.name).substring(0, 2) || "U"}
              </AvatarFallback>
            </Link>
          </Avatar>
        </div>
      </header>

      <Suspense fallback={<Loader2 className="m-auto animate-spin" />}>
        <main className="flex-1 overflow-auto">
          <AnimatedContentWrapper
            isAnimating={isAnimating}
            direction={animationDirection}
            onAnimationEnd={resetAnimation}
          >
            {children}
          </AnimatedContentWrapper>
        </main>
      </Suspense>

      <footer className="sticky bottom-0 w-full overflow-x-auto border-t bg-background p-2 z-40 rounded-t-xl">
        <NavUser />
      </footer>
    </div>
  );
};

export default HomeLayout;
